"use client";

import React from "react";
import Image from "next/image";
import { FaCheckCircle } from "react-icons/fa";

const WhyChooseUs = ({ data }) => {
  const {
    buildSubtitle = "WHAT WE BUILD",
    buildTitle = "Flutter Solutions for Every Business Need",
    buildCards = [],
    processSubtitle = "OUR DEVELOPMENT PROCESS",
    processTitle = "Our Proven Flutter Development Process",
    processSteps = [],
    benefitsSubtitle = "WHY CHOOSE FLUTTER?",
    benefitsTitle = "Why Flutter is the Right Choice",
    benefits = [],
    logoImage = "/images/Flutter_logo_img.png",
    whiteLogoImage = "",
  } = data || {};

  return (
    <section className="w-full bg-background-primary text-text-primary px-6! md:px-12!  relative overflow-hidden font-sans">
      <div className="absolute top-1/4 left-0 w-[500px] h-[500px] blur-[150px] -z-10 rounded-full bg-indigo-500/5 dark:bg-indigo-900/20" />
      <div className="absolute bottom-1/4 right-0 w-[600px] h-[600px] blur-[180px] -z-10 rounded-full bg-blue-500/5 dark:bg-blue-900/20" />

      <div className="max-w-8xl mx-auto! mt-10!">
        <div className="mb-10!">
          {buildSubtitle && <span className="text-[#8b5cf6] text-xs font-bold uppercase tracking-widest block mb-3!">{buildSubtitle}</span>}
          {buildTitle && <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-10! text-text-primary">{buildTitle}</h2>}

          {buildCards.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-6">
              {buildCards.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className={`group relative rounded-2xl border p-6! transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_10px_30px_rgba(139,92,246,0.1)] ${card.borderClass || "border-border-secondary"}`}
                    style={{
                      background: "var(--card-background)",
                      boxShadow: "var(--card-shadow)",
                    }}
                  >
                    {Icon && (
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-xl border border-border-primary dark:border-border-secondary ${card.bgClass || ""} ${card.colorClass || ""} mb-6! transition-all duration-300 ${card.glowClass || ""}`}
                      >
                        <Icon size={22} />
                      </div>
                    )}

                    <h3 className="text-lg font-bold text-text-primary mb-3! leading-snug">{card.title}</h3>
                    <p className="text-xs text-text-secondary leading-relaxed font-light">{card.desc}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div
            className={`lg:col-span-8 border rounded-2xl p-8! md:p-10! relative overflow-hidden border-border-primary`}
            style={{
              background: "var(--card-background)",
              boxShadow: "var(--card-shadow)",
            }}
          >
            {processSubtitle && <span className="text-[#8b5cf6] text-xs font-bold uppercase tracking-widest block mb-3!">{processSubtitle}</span>}
            {processTitle && <h3 className="text-2xl md:text-3xl font-bold text-text-primary mb-10!">{processTitle}</h3>}

            {processSteps.length > 0 && (
              <div className="relative mt-12!">
                <div className="absolute top-7 left-[8.3%] right-[8.3%] hidden h-[2px] border-t border-dashed border-border-primary lg:block z-0" />

                <div className="absolute left-7 top-7 bottom-7 w-[2px] border-l border-dashed border-border-primary lg:hidden z-0" />

                <div className="grid grid-cols-1 gap-10 md:grid-cols-3 lg:grid-cols-6 gap-y-10 lg:gap-x-4 relative z-10">
                  {processSteps.map((stepItem, index) => {
                    const StepIcon = stepItem.icon;
                    return (
                      <div key={index} className="flex flex-row items-start gap-4 lg:flex-col lg:items-center lg:text-center lg:gap-0 group relative">
                        {StepIcon && (
                          <div
                            className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-blue-500/30 dark:border-border-secondary bg-background-primary dark:bg-background-secondary shadow-[0_0_15px_rgba(59,130,246,0.1)] dark:shadow-[0_0_15px_rgba(59,130,246,0.15)] group-hover:border-blue-500 dark:group-hover:border-blue-400 group-hover:shadow-[0_0_20px_rgba(59,130,246,0.2)] dark:group-hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-all duration-300 lg:mb-4! z-10"
                          >
                            <StepIcon size={20} className="text-[#3b82f6] group-hover:text-[#22d3ee] transition-colors" />
                          </div>
                        )}

                        {index < processSteps.length - 1 && (
                          <span
                            className="hidden lg:block absolute top-[24px] right-0 translate-x-1/2 h-2 w-2 rounded-full bg-cyan-400 shadow-[0_0_8px_#22d3ee] border border-background-primary dark:border-[#030214] z-20"
                          />
                        )}

                        <div className="flex-1 lg:mt-2!">
                          <div className="flex flex-col items-start lg:items-center">
                            <span className="text-xs font-bold text-cyan-500 dark:text-cyan-400">{stepItem.step}</span>

                            <h4 className="text-sm font-bold text-text-primary">{stepItem.title}</h4>
                          </div>
                          <p className="mt-2! text-[14px] leading-relaxed text-text-secondary font-light max-w-[200px] lg:mx-auto!">{stepItem.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div
            className="lg:col-span-4 border rounded-2xl p-8! md:p-10! relative overflow-hidden flex flex-col justify-between border-border-primary dark:border-border-secondary benefits-card"
          >
            <div>
              {benefitsSubtitle && <span className="text-white text-xs font-bold uppercase tracking-widest block mb-3!">{benefitsSubtitle}</span>}
              {benefitsTitle && <h3 className="text-2xl md:text-3xl font-bold text-white mb-10!">{benefitsTitle}</h3>}
            </div>

            {benefits.length > 0 && (
              <div className="space-y-4 z-10 relative">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3 group pb-2!">
                    <FaCheckCircle className="text-lime-300 shrink-0 mt-1.5! group-hover:text-lime-500 transition-colors" />
                    <p className="text-[16px] md:text-[14px] 2xl:text-[18px] text-white font-light group-hover:text-text-secondary transition-colors leading-snug">{benefit}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-8px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .benefits-card {
          background: linear-gradient(135deg, #60a5fa 0%, #6366f1 50%, #8b5cf6 100%);
        }
        [data-theme="dark"] .benefits-card {
          background: linear-gradient(135deg, #1e3a8a 0%, #4338ca 50%, #9333ea 100%);
        }
      `}</style>
    </section>
  );
};

export default WhyChooseUs;
